"use client"
import GCTextElement from "@/app/(site)/_components/GCMolecules/GCTextElement"
import { useProductStore } from "@/app/(site)/contexts/Global"
import { removeTextFromSize } from "@/lib/utils"
import { viewItemDepictEvent } from "@/lib/utils/tracking/depictEvents"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { useCookieBot } from "@/src/hooks/useCookieBot"
import { clsx } from "@/src/utils/clsx"
import { getLangCode } from "@/src/utils/string/getLangCode"
import { resolveSanityInjectGeneralShortText } from "@/src/utils/freeText/resolveSanityInjectGeneralShortText"
import { t } from "@/translations"
import React, { useCallback, useEffect } from "react"

type SizeChartProps = {
    sizes: Size[]
    setErrorStyles: React.Dispatch<React.SetStateAction<boolean>>
    errorStyles: boolean
    firstAvailableSize?: Size
    currentProduct?: any
    locale: string
    isOneSize: boolean
}

export type Size = {
    sizeId: string
    item: string
    name: string
    sku: string
    isAvailable: boolean
    isSelected: boolean
}

export default function SizeChart({
    sizes,
    errorStyles,
    setErrorStyles,
    firstAvailableSize,
    currentProduct,
    locale,
    isOneSize
}: SizeChartProps) {
    const availableSizesContext = useProductStore(state => state.availableSizes)
    const setAvailableSizes = useProductStore(state => state.setAvailableSizes)
    const selectSize = useProductStore(state => state.selectSize)
    const updateCanAddToCart = useProductStore(state => state.updateCanAddToCart)
    const { consent } = useCookieBot()
    const siteContext = useSiteContext()
    const langCode = getLangCode(locale) || "en"

    const sanityFreeTextOverride = resolveSanityInjectGeneralShortText(
        currentProduct?.sanity_inject_general_short_text_json,
        langCode,
        locale
    )
    const generalShortText =
        sanityFreeTextOverride ?? currentProduct?.general_short_text_title ?? ""
    useEffect(() => {
        // Only set available sizes if Redux is empty (first mount) and we have sizes
        if (availableSizesContext.length === 0 && sizes.length > 0) {
            const mergedSizes = sizes.map(size => ({
                ...size,
                isSelected: firstAvailableSize?.name === size.name
            }))

            setAvailableSizes(mergedSizes)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizes.length, availableSizesContext.length, JSON.stringify(firstAvailableSize)])

    const handleSelectSize = useCallback(
        (selectedSize: Size, initialSelectedSize?: Size[]) => {
            // Track size change
            if (currentProduct) {
                import("@/lib/utils/tracking/gtmEvents").then(
                    ({ pdpChangeSkuEvent, viewItemEvent }) => {
                        pdpChangeSkuEvent(
                            "change_size",
                            currentProduct?.color_swatch?.color_name ?? "",
                            siteContext,
                            currentProduct
                        )
                        viewItemEvent(
                            currentProduct,
                            {
                                size: selectedSize.name,
                                sku: selectedSize.sku,
                                item: selectedSize.item
                            },
                            siteContext,
                            "Change Size"
                        )
                    }
                )
                if (consent.necessary) {
                    viewItemDepictEvent(currentProduct, {
                        size: selectedSize.name,
                        sku: selectedSize.sku
                    })
                }
            }

            const updatedSizes = sizes.map(s => {
                const isAvailable = initialSelectedSize
                    ? initialSelectedSize.some(
                          initialSize =>
                              initialSize.name === s.name && initialSize.isAvailable
                      )
                    : s.isAvailable

                return {
                    ...s,
                    isSelected: s.name === selectedSize.name,
                    isAvailable
                }
            })
            setAvailableSizes(updatedSizes)
            selectSize(selectedSize.name)
            updateCanAddToCart()
            setErrorStyles(false)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentProduct, siteContext, availableSizesContext, consent.necessary, sizes]
    )

    const buttonSelectedClasses =
        "before:absolute before:-inset-px before:z-10 before:border before:border-main-black"
    const buttonHoverClasses =
        "hover:before:absolute hover:before:-inset-px hover:before:z-10 hover:before:border hover:before:border-main-black"

    const isSelectedSizeOutOfStock = availableSizesContext.some(
        size => !size.isAvailable && size.isSelected
    )

    const isNotSelectedSize = availableSizesContext.every(size => !size.isSelected)

    const trimSizeName = (sizeName: string) => {
        return sizeName.split("/")[0].trim()
    }

    return (
        <div className="flex flex-col gap-12">
            <div
                className={clsx("grid w-full", isOneSize ? "grid-cols-1" : "grid-cols-4")}
            >
                {Array.isArray(availableSizesContext) && availableSizesContext.length > 0
                    ? availableSizesContext.map(size => (
                          <button
                              type="button"
                              key={size.sizeId}
                              className={clsx(
                                  "relative -mb-px -mr-px box-border flex items-center justify-start whitespace-nowrap border border-border-neutral px-12 py-8 text-left",
                                  size.isSelected && buttonSelectedClasses,
                                  !size.isSelected && buttonHoverClasses,
                                  isOneSize && "w-fit"
                              )}
                              aria-label={trimSizeName(size.name)}
                              onClick={() => handleSelectSize(size, sizes)}
                          >
                              <GCTextElement
                                  text={removeTextFromSize(size.name)}
                                  as="span"
                                  className={`${!size.isAvailable && "text-secondary-text line-through"} font_trimMonoPoc_Regular_12_120_uppercase`}
                              />
                          </button>
                      ))
                    : sizes.map(size => (
                          <button
                              type="button"
                              key={size.sizeId}
                              className={clsx(
                                  "relative -mb-px -mr-px box-border flex items-center justify-start whitespace-nowrap border border-border-neutral px-12 py-8 text-left",
                                  size.isSelected && buttonSelectedClasses,
                                  !size.isSelected && buttonHoverClasses,
                                  isOneSize && "w-fit"
                              )}
                              aria-label={size.name}
                              onClick={() => handleSelectSize(size, sizes)}
                          >
                              <GCTextElement
                                  text={trimSizeName(size.name)}
                                  as="span"
                                  className={`${!size.isAvailable && "text-secondary-text line-through"} font_trimMonoPoc_Regular_12_120_uppercase`}
                              />
                          </button>
                      ))}
            </div>
            <div className="flex flex-col gap-4 lg:gap-0">
                {isSelectedSizeOutOfStock && (
                    <GCTextElement
                        text={t(langCode, "product_page.out_of_stock")}
                        as="p"
                        className="text-col-text-alert Trade_Gothic_Text_12_150_0_300"
                    />
                )}

                {isNotSelectedSize && errorStyles && (
                    <GCTextElement
                        text={t(langCode, "product_page.select_size")}
                        as="p"
                        className="text-col-text-alert Trade_Gothic_Text_12_150_0_300"
                    />
                )}
                <GCTextElement
                    text={generalShortText}
                    as="p"
                    className="text-secondary-text Trade_Gothic_Text_12_150_0_300"
                />
            </div>
        </div>
    )
}
